import defaultMobileUiDocument from "../services/login-api/src/config/mobile-ui.v2.json";
import type {
  MobileTab,
  MobileProfileView,
  MobileUiAction,
  MobileUiActionName,
  MobileUiCompanionField,
  MobileUiCompanionFieldKey,
  MobileUiComponent,
  MobileUiComponentOption,
  MobileUiComponentKind,
  MobileUiConfig,
  MobileUiIcon,
  MobileUiLayout,
  MobileManagerCapabilityInventory,
  MobileManagerCapabilityState,
  MobileManagerWorkspaceKey,
  MobileUiScreen,
  MobileUiSlot,
  MobileUiSummaryItem,
  MobileUiTab,
  MobileUiTone,
  MobileUiV1Config
} from "./types";

const MOBILE_TABS: MobileTab[] = ["home", "inbox", "olivia", "create", "profile"];
const MOBILE_ICONS: MobileUiIcon[] = [
  "home",
  "inbox",
  "sparkles",
  "square-plus",
  "user-round",
  "refresh-cw",
  "arrow-left",
  "arrow-right",
  "log-out",
  "chevron-right",
  "wifi-off",
  "shield-check",
  "message-circle",
  "circle-alert",
  "circle-check",
  "clock",
  "image",
  "settings",
  "link",
  "bell",
  "file-text",
  "database",
  "plug",
  "plus",
  "trash-2",
  "chevron-up",
  "chevron-down",
  "save",
  "user-cog",
  "send-horizontal",
  "list-filter",
  "search",
  "tag",
  "paperclip",
  "users",
  "x"
];
const MOBILE_ACTIONS: MobileUiActionName[] = [
  "navigate",
  "open-conversation",
  "back-to-inbox",
  "refresh-inbox",
  "retry-session",
  "retry-inbox",
  "retry-conversation",
  "load-more-conversations",
  "load-earlier-messages",
  "toggle-inbox-filters",
  "close-inbox-filters",
  "reset-inbox-filters",
  "retry-inbox-metadata",
  "retry-wctek",
  "retry-wctek-history",
  "new-wctek-session",
  "open-wctek-session",
  "retry-wctek-send",
  "cancel-wctek-send",
  "retry-companion-settings",
  "open-profile-view",
  "retry-public-profile",
  "open-public-profile",
  "retry-social-links",
  "add-social-link",
  "move-social-link",
  "remove-social-link",
  "retry-checkins",
  "use-device-timezone",
  "reset-checkins",
  "retry-review-drafts",
  "select-review-draft",
  "reject-review-draft",
  "human-takeover-review-draft",
  "retry-memory-hydration",
  "retry-provider-connections",
  "execute-provider-action",
  "open-auth-security",
  "logout"
];
const MOBILE_COMPONENTS: MobileUiComponentKind[] = [
  "app-header",
  "bottom-navigation",
  "connection-status",
  "account-overview",
  "inbox-preview",
  "summary-grid",
  "screen-heading",
  "conversation-list",
  "conversation-detail",
  "wctek-chat",
  "placeholder",
  "profile-summary",
  "companion-settings",
  "social-links-settings",
  "checkin-settings",
  "messaging-review",
  "memory-hydration",
  "provider-connections",
  "session-actions"
];
const MOBILE_PROFILE_VIEWS: MobileProfileView[] = [
  "public",
  "hub",
  "account",
  "ai-manager",
  "ai-customize",
  "ai-checkins",
  "ai-messaging",
  "ai-memory",
  "ai-connections"
];
const MOBILE_MANAGER_WORKSPACES: MobileManagerWorkspaceKey[] = [
  "overview",
  "funnels",
  "create",
  "scheduler",
  "public-profile",
  "account-security",
  "companion"
];
const SOCIAL_PLATFORM_KEYS = [
  "instagram",
  "tiktok",
  "facebook",
  "x",
  "threads",
  "snapchat",
  "bluesky",
  "mastodon",
  "pinterest",
  "youtube",
  "twitch",
  "kick",
  "discord",
  "reddit",
  "linkedin",
  "github",
  "spotify",
  "apple-music",
  "soundcloud",
  "bandcamp",
  "patreon",
  "ko-fi",
  "buy-me-a-coffee",
  "paypal",
  "venmo",
  "cash-app",
  "custom"
] as const;
const MOBILE_TONES: MobileUiTone[] = ["neutral", "accent", "success", "warning", "danger"];
const MOBILE_SLOTS: MobileUiSlot[] = ["primary", "detail"];
const LABEL_KEYS = [
  "active",
  "add",
  "assistantAuthor",
  "acceptPolicies",
  "ariaLabel",
  "activeFilters",
  "allChannels",
  "allStatuses",
  "applyFilters",
  "back",
  "cancel",
  "chooseFile",
  "channel",
  "closeFilters",
  "closed",
  "companionChannel",
  "companionState",
  "composerLabel",
  "composerPlaceholder",
  "confirmHumanTakeover",
  "confirmReject",
  "confirmReset",
  "consentHelp",
  "conversationUpload",
  "desktopUpload",
  "defaultView",
  "disabled",
  "discardDraft",
  "discardLogout",
  "draftText",
  "deletedPreview",
  "enabled",
  "empty",
  "emptyConversation",
  "fileTooLarge",
  "filters",
  "folders",
  "frequency",
  "frequencyOff",
  "frequencyOne",
  "frequencyThree",
  "frequencyTwo",
  "humanTakeover",
  "imessageChannel",
  "instagramChannel",
  "label",
  "lastUpdated",
  "labels",
  "latestInbound",
  "latestMessage",
  "loadEarlier",
  "loadHistory",
  "loadMore",
  "loading",
  "loadingHistory",
  "memoryCells",
  "memoryStatus",
  "messageEmpty",
  "messagesScored",
  "metadataUnavailable",
  "noLabels",
  "moveDown",
  "moveUp",
  "newConversation",
  "noSelection",
  "noSend",
  "offline",
  "offlineChat",
  "offlineSave",
  "open",
  "online",
  "pausedUntil",
  "platform",
  "policyAccepted",
  "policyHeading",
  "policyRequired",
  "policyUnavailable",
  "profileGrounding",
  "quietEnd",
  "quietHours",
  "quietStart",
  "recentConversations",
  "rcsChannel",
  "resetFilters",
  "replyBody",
  "replyTitle",
  "reject",
  "reload",
  "remove",
  "reset",
  "retry",
  "retrySend",
  "save",
  "saveDraft",
  "saved",
  "saving",
  "search",
  "searchPlaceholder",
  "sendMessage",
  "sending",
  "sessionStatus",
  "settingsAriaLabel",
  "signedInAs",
  "slotAfternoon",
  "slotEvening",
  "slotMorning",
  "specialists",
  "styleLearning",
  "status",
  "sort",
  "smsChannel",
  "timezone",
  "upload",
  "uploadComplete",
  "uploadConsent",
  "uploading",
  "historyReview",
  "historyReviewBody",
  "importedHistory",
  "partialHistory",
  "attachment",
  "contactHistory",
  "tiktokChannel",
  "unsaved",
  "unsavedLogout",
  "url",
  "useDeviceTimezone",
  "visible",
  "workspace",
  "xChannel",
  "you"
] as const;
const BODY_LABEL_KEYS = new Set(["consentHelp", "desktopUpload", "disabled", "historyReviewBody", "offlineChat", "policyUnavailable", "replyBody"]);

const COMPANION_FIELD_KEYS: MobileUiCompanionFieldKey[] = [
  "ai-name",
  "base-personality",
  "communication-style",
  "hard-refusals",
  "soft-boundaries",
  "relationship-boundaries",
  "ai-reminder-frequency",
  "redirect-to-real-life"
];

export const MOBILE_COMPANION_FIELD_OPTIONS: Partial<Record<MobileUiCompanionFieldKey, readonly string[]>> = Object.freeze({
  "communication-style": ["warm", "balanced", "direct", "playful", "calm"],
  "relationship-boundaries": ["non_sexual_non_possessive", "friendly", "professional", "creator_fan"],
  "ai-reminder-frequency": ["low", "balanced", "high"],
  "redirect-to-real-life": ["low", "balanced", "high"]
});

export const MOBILE_SOCIAL_PLATFORM_OPTIONS = Object.freeze([...SOCIAL_PLATFORM_KEYS]);

export const MOBILE_UI_LIMITS = Object.freeze({
  responseBytes: 64 * 1024,
  screens: 5,
  componentsPerScreen: 12,
  summaryItems: 6,
  labelsPerComponent: LABEL_KEYS.length,
  identifier: 64,
  label: 80,
  title: 100,
  body: 280
});

export const SUPPORTED_MOBILE_UI_ACTIONS = Object.freeze([...MOBILE_ACTIONS]);
export const SUPPORTED_MOBILE_UI_COMPONENTS = Object.freeze([...MOBILE_COMPONENTS]);
export const DEFAULT_MOBILE_UI = defaultMobileUiDocument as MobileUiConfig;

function plainObject(value: unknown): value is Record<string, any> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function utf8ByteLength(value: string) {
  let bytes = 0;
  for (const character of value) {
    const codePoint = character.codePointAt(0) || 0;
    bytes += codePoint <= 0x7f ? 1 : codePoint <= 0x7ff ? 2 : codePoint <= 0xffff ? 3 : 4;
  }
  return bytes;
}

function text(value: unknown, fallback: string, limit: number = MOBILE_UI_LIMITS.body) {
  const normalized = String(value ?? "")
    .replace(/[\u0000-\u001f\u007f]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return normalized ? normalized.slice(0, limit) : fallback;
}

function identifier(value: unknown, fallback: string) {
  const normalized = String(value || "").trim();
  return /^[a-z][a-z0-9-]{0,63}$/.test(normalized) ? normalized : fallback;
}

function tabKey(value: unknown): MobileTab | null {
  const key = String(value || "");
  return MOBILE_TABS.includes(key as MobileTab) ? key as MobileTab : null;
}

function profileViewName(value: unknown): MobileProfileView | null {
  const name = String(value || "");
  return MOBILE_PROFILE_VIEWS.includes(name as MobileProfileView) ? name as MobileProfileView : null;
}

function iconName(value: unknown, fallback: MobileUiIcon): MobileUiIcon {
  return MOBILE_ICONS.includes(value as MobileUiIcon) ? value as MobileUiIcon : fallback;
}

function toneName(value: unknown, fallback: MobileUiTone = "neutral"): MobileUiTone {
  return MOBILE_TONES.includes(value as MobileUiTone) ? value as MobileUiTone : fallback;
}

function slotName(value: unknown, fallback: MobileUiSlot = "primary"): MobileUiSlot {
  return MOBILE_SLOTS.includes(value as MobileUiSlot) ? value as MobileUiSlot : fallback;
}

function capability(value: unknown, fallback: MobileUiConfig["capabilities"]["send"]) {
  return value === "available" || value === "read_only" || value === "unavailable" ? value : fallback;
}

function managerCapabilityState(value: unknown, fallback: MobileManagerCapabilityState) {
  return value === "available" || value === "read_only" || value === "unavailable" ? value : fallback;
}

function normalizeManagerCapabilities(value: unknown): MobileManagerCapabilityInventory {
  const fallback = DEFAULT_MOBILE_UI.managerCapabilities;
  const source = plainObject(value) && value.schemaVersion === "mobile-manager-capabilities.v1" ? value : {};
  const sourceWorkspaces = Array.isArray(source.workspaces) ? source.workspaces : [];
  return {
    schemaVersion: "mobile-manager-capabilities.v1",
    workspaces: MOBILE_MANAGER_WORKSPACES.map((workspaceKey) => {
      const base = fallback.workspaces.find((workspace) => workspace.key === workspaceKey)!;
      const requested = sourceWorkspaces.find((workspace) => plainObject(workspace) && workspace.key === workspaceKey);
      const requestedOperations = plainObject(requested) && Array.isArray(requested.operations) ? requested.operations : [];
      return {
        key: workspaceKey,
        operations: base.operations.map((operation) => {
          const candidate = requestedOperations.find((item) => plainObject(item) && item.key === operation.key);
          const requestedState = plainObject(candidate)
            ? managerCapabilityState(candidate.state, operation.state)
            : operation.state;
          const state = operation.clientSupported ? requestedState : "unavailable";
          return {
            key: operation.key,
            binding: operation.binding,
            clientSupported: operation.clientSupported,
            state,
            reasonCode: state === "available"
              ? null
              : text(
                plainObject(candidate) ? candidate.reasonCode : operation.reasonCode,
                operation.reasonCode || "MOBILE_MANAGER_CAPABILITY_UNAVAILABLE",
                100
              ).replace(/[^A-Z0-9_]/g, "_")
          };
        })
      };
    })
  };
}

export function isSupportedMobileUiAction(value: unknown): value is MobileUiActionName {
  return MOBILE_ACTIONS.includes(value as MobileUiActionName);
}

export function isSupportedMobileUiComponent(value: unknown): value is MobileUiComponentKind {
  return MOBILE_COMPONENTS.includes(value as MobileUiComponentKind);
}

export function isSupportedMobileUiCompanionField(value: unknown): value is MobileUiCompanionFieldKey {
  return COMPANION_FIELD_KEYS.includes(value as MobileUiCompanionFieldKey);
}

function cloneAction(action: MobileUiAction | null | undefined): MobileUiAction | null {
  return action ? { ...action } : null;
}

function cloneCompanionField(field: MobileUiCompanionField): MobileUiCompanionField {
  return {
    ...field,
    options: field.options?.map((option) => ({ ...option }))
  };
}

export function normalizeMobileUiAction(value: unknown, fallback: MobileUiAction | null = null): MobileUiAction | null {
  if (value === undefined || value === null) return cloneAction(fallback);
  if (!plainObject(value) || !isSupportedMobileUiAction(value.name)) return null;
  const tab = tabKey(value.tab);
  const profileView = profileViewName(value.profileView);
  if (value.name === "navigate" && !tab) return null;
  if (value.name === "open-profile-view" && !profileView) return null;
  return {
    name: value.name,
    label: text(value.label, fallback?.label || value.name, MOBILE_UI_LIMITS.label),
    icon: iconName(value.icon, fallback?.icon || "arrow-right"),
    tab: value.name === "navigate" ? tab : null,
    profileView: value.name === "open-profile-view" ? profileView : null
  };
}

function normalizeLabels(value: unknown, fallback: Record<string, string> = {}) {
  const source = plainObject(value) ? value : {};
  const labels: Record<string, string> = {};
  for (const key of LABEL_KEYS) {
    if (source[key] !== undefined || fallback[key]) {
      labels[key] = text(
        source[key],
        fallback[key] || key,
        BODY_LABEL_KEYS.has(key) ? MOBILE_UI_LIMITS.body : MOBILE_UI_LIMITS.title
      );
    }
  }
  return labels;
}

function companionFieldKey(value: unknown): MobileUiCompanionFieldKey | null {
  const key = String(value || "");
  return isSupportedMobileUiCompanionField(key) ? key : null;
}

function normalizeCompanionFieldOptions(value: unknown, fallback: MobileUiCompanionField | undefined, key: MobileUiCompanionFieldKey) {
  const allowed = MOBILE_COMPANION_FIELD_OPTIONS[key];
  if (!allowed) return undefined;
  const source = Array.isArray(value) ? value : [];
  const requested = new Map<string, unknown>();
  for (const option of source) {
    if (plainObject(option) && allowed.includes(String(option.value))) requested.set(String(option.value), option.label);
  }
  const fallbackLabels = new Map((fallback?.options || []).map((option) => [option.value, option.label]));
  return allowed.map((optionValue) => ({
    value: optionValue,
    label: text(requested.get(optionValue), fallbackLabels.get(optionValue) || optionValue, MOBILE_UI_LIMITS.label)
  }));
}

function normalizeCompanionFields(value: unknown, fallback: MobileUiCompanionField[] = []) {
  if (!Array.isArray(value)) return fallback.map(cloneCompanionField);
  const fields: MobileUiCompanionField[] = [];
  const seen = new Set<MobileUiCompanionFieldKey>();
  for (const rawField of value.slice(0, COMPANION_FIELD_KEYS.length)) {
    if (!plainObject(rawField)) continue;
    const key = companionFieldKey(rawField.key);
    if (!key || seen.has(key)) continue;
    const base = fallback.find((field) => field.key === key);
    seen.add(key);
    fields.push({
      key,
      label: text(rawField.label, base?.label || key, MOBILE_UI_LIMITS.title),
      help: text(rawField.help, base?.help || "", MOBILE_UI_LIMITS.body),
      placeholder: text(rawField.placeholder, base?.placeholder || "", MOBILE_UI_LIMITS.body),
      options: normalizeCompanionFieldOptions(rawField.options, base, key)
    });
  }
  return fields.length ? fields : fallback.map(cloneCompanionField);
}

function cloneComponentOption(option: MobileUiComponentOption): MobileUiComponentOption {
  return { ...option };
}

function normalizeSocialPlatformOptions(value: unknown, fallback: MobileUiComponentOption[] = []) {
  if (!Array.isArray(value)) return fallback.map(cloneComponentOption);
  const labels = new Map<string, unknown>();
  for (const option of value.slice(0, SOCIAL_PLATFORM_KEYS.length)) {
    if (!plainObject(option)) continue;
    const key = String(option.value || "");
    if (SOCIAL_PLATFORM_KEYS.includes(key as typeof SOCIAL_PLATFORM_KEYS[number]) && !labels.has(key)) {
      labels.set(key, option.label);
    }
  }
  const normalized = SOCIAL_PLATFORM_KEYS
    .filter((key) => labels.has(key))
    .map((key) => ({
      value: key,
      label: text(labels.get(key), fallback.find((option) => option.value === key)?.label || key, MOBILE_UI_LIMITS.label)
    }));
  return normalized.length ? normalized : fallback.map(cloneComponentOption);
}

function normalizeSummaryItems(value: unknown, fallback: MobileUiSummaryItem[] = []) {
  if (!Array.isArray(value)) return fallback.map((item) => ({ ...item, action: cloneAction(item.action) }));
  const items: MobileUiSummaryItem[] = [];
  for (const [index, rawItem] of value.slice(0, MOBILE_UI_LIMITS.summaryItems).entries()) {
    if (!plainObject(rawItem)) continue;
    const base = fallback[index] || fallback[0];
    const key = identifier(rawItem.key, base?.key || `item-${index + 1}`);
    const title = text(rawItem.title, base?.title || "Workspace", MOBILE_UI_LIMITS.title);
    const body = text(rawItem.body, base?.body || "Open this workspace area.", MOBILE_UI_LIMITS.body);
    items.push({
      key,
      title,
      status: text(rawItem.status, base?.status || "Available", MOBILE_UI_LIMITS.label),
      body,
      icon: iconName(rawItem.icon, base?.icon || "circle-check"),
      tone: toneName(rawItem.tone, base?.tone || "neutral"),
      titleSource: rawItem.titleSource === "active-companion-name"
        ? "active-companion-name"
        : base?.titleSource || null,
      action: normalizeMobileUiAction(rawItem.action, base?.action || null)
    });
  }
  return items.length ? items : fallback.map((item) => ({ ...item, action: cloneAction(item.action) }));
}

function normalizeComponent(value: unknown, fallback: MobileUiComponent | undefined, index: number): MobileUiComponent | null {
  if (!plainObject(value) || !isSupportedMobileUiComponent(value.kind)) return null;
  const kind = value.kind;
  const base = fallback?.kind === kind ? fallback : undefined;
  const normalized: MobileUiComponent = {
    id: identifier(value.id, base?.id || `${kind}-${index + 1}`),
    kind,
    slot: slotName(value.slot, base?.slot || "primary"),
    visible: value.visible !== false
  };

  if (value.profileView !== undefined || base?.profileView) {
    normalized.profileView = profileViewName(value.profileView) || base?.profileView || null;
  }

  if (value.eyebrow !== undefined || base?.eyebrow) {
    normalized.eyebrow = text(value.eyebrow, base?.eyebrow || "", MOBILE_UI_LIMITS.label);
  }
  if (value.title !== undefined || base?.title) {
    normalized.title = text(value.title, base?.title || "", MOBILE_UI_LIMITS.title);
  }
  if (value.body !== undefined || base?.body) {
    normalized.body = text(value.body, base?.body || "", MOBILE_UI_LIMITS.body);
  }
  if (value.icon !== undefined || base?.icon) {
    normalized.icon = iconName(value.icon, base?.icon || "circle-check");
  }
  if (value.tone !== undefined || base?.tone) {
    normalized.tone = toneName(value.tone, base?.tone || "neutral");
  }
  if (value.maxItems !== undefined || base?.maxItems) {
    const requested = Number(value.maxItems || base?.maxItems || 3);
    normalized.maxItems = Math.max(1, Math.min(6, Number.isFinite(requested) ? Math.floor(requested) : 3));
  }
  normalized.labels = normalizeLabels(value.labels, base?.labels);
  normalized.action = normalizeMobileUiAction(value.action, base?.action || null);
  normalized.secondaryAction = normalizeMobileUiAction(value.secondaryAction, base?.secondaryAction || null);
  if (kind === "summary-grid") normalized.items = normalizeSummaryItems(value.items, base?.items);
  if (kind === "companion-settings") normalized.fields = normalizeCompanionFields(value.fields, base?.fields);
  if (kind === "social-links-settings") normalized.options = normalizeSocialPlatformOptions(value.options, base?.options);
  return normalized;
}

function componentFallback(screen: MobileUiScreen, value: unknown, index: number) {
  if (!plainObject(value)) return screen.components[index];
  const id = String(value.id || "");
  const kind = String(value.kind || "");
  return screen.components.find((component) => component.id === id)
    || screen.components.find((component) => component.kind === kind)
    || screen.components[index];
}

function normalizeScreen(value: unknown, key: MobileTab): MobileUiScreen {
  const fallback = DEFAULT_MOBILE_UI.screens[key];
  const source = plainObject(value) ? value : {};
  const rawComponents = Array.isArray(source.components) ? source.components.slice(0, MOBILE_UI_LIMITS.componentsPerScreen) : [];
  const components: MobileUiComponent[] = [];
  const ids = new Set<string>();

  for (const [index, rawComponent] of rawComponents.entries()) {
    const normalized = normalizeComponent(rawComponent, componentFallback(fallback, rawComponent, index), index);
    if (!normalized || ids.has(normalized.id) || normalized.visible === false) continue;
    ids.add(normalized.id);
    components.push(normalized);
  }

  return {
    key,
    title: text(source.title, fallback.title, MOBILE_UI_LIMITS.title),
    layout: key === "inbox" && source.layout === "inbox" ? "inbox" : "stack",
    components: components.length
      ? components
      : fallback.components.map((component) => ({
        ...component,
        labels: { ...(component.labels || {}) },
        action: cloneAction(component.action),
        secondaryAction: cloneAction(component.secondaryAction),
        items: component.items?.map((item) => ({ ...item, action: cloneAction(item.action) })),
        fields: component.fields?.map(cloneCompanionField),
        options: component.options?.map(cloneComponentOption)
      }))
  };
}

function normalizeTabs(value: unknown): MobileUiTab[] {
  const source = Array.isArray(value) ? value : [];
  const byKey = new Map<MobileTab, Record<string, any>>();
  for (const tab of source.slice(0, MOBILE_TABS.length)) {
    const key = tabKey(tab?.key);
    if (key && !byKey.has(key)) byKey.set(key, tab);
  }
  return MOBILE_TABS.map((key) => {
    const fallback = DEFAULT_MOBILE_UI.tabs.find((tab) => tab.key === key)!;
    const tab = byKey.get(key) || {};
    return {
      key,
      label: text(tab.label, fallback.label, 24),
      icon: iconName(tab.icon, fallback.icon)
    };
  });
}

function normalizeChrome(value: unknown) {
  const source = plainObject(value) ? value : {};
  const header = normalizeComponent(source.header, DEFAULT_MOBILE_UI.chrome.header, 0);
  const navigation = normalizeComponent(source.navigation, DEFAULT_MOBILE_UI.chrome.navigation, 0);
  return {
    header: header?.kind === "app-header" ? header : { ...DEFAULT_MOBILE_UI.chrome.header },
    navigation: navigation?.kind === "bottom-navigation" ? navigation : { ...DEFAULT_MOBILE_UI.chrome.navigation }
  };
}

function cloneDefault(): MobileUiConfig {
  return JSON.parse(JSON.stringify(DEFAULT_MOBILE_UI));
}

function normalizeV2(source: Record<string, any>): MobileUiConfig {
  const app = plainObject(source.app) ? source.app : {};
  const signIn = plainObject(app.signIn) ? app.signIn : {};
  const noAccount = plainObject(app.noAccount) ? app.noAccount : {};
  const copy = plainObject(app.copy) ? app.copy : {};
  const fallbackCopy = DEFAULT_MOBILE_UI.app.copy;
  const screens = {} as Record<MobileTab, MobileUiScreen>;
  for (const tab of MOBILE_TABS) screens[tab] = normalizeScreen(source.screens?.[tab], tab);

  return {
    schemaVersion: "mobile-ui.v2",
    revision: text(source.revision, DEFAULT_MOBILE_UI.revision, 80).replace(/[^A-Za-z0-9_.-]/g, "-") || DEFAULT_MOBILE_UI.revision,
    theme: {
      accent: source.theme?.accent === "blue" || source.theme?.accent === "coral" ? source.theme.accent : "jade",
      appearance: source.theme?.appearance === "light" || source.theme?.appearance === "dark" ? source.theme.appearance : "system",
      density: source.theme?.density === "compact" ? "compact" : "comfortable"
    },
    app: {
      brandLabel: text(app.brandLabel, DEFAULT_MOBILE_UI.app.brandLabel, 40),
      productLabel: text(app.productLabel, DEFAULT_MOBILE_UI.app.productLabel, 60),
      loadingMessage: text(app.loadingMessage, DEFAULT_MOBILE_UI.app.loadingMessage, 120),
      signIn: {
        eyebrow: text(signIn.eyebrow, DEFAULT_MOBILE_UI.app.signIn.eyebrow, 60),
        title: text(signIn.title, DEFAULT_MOBILE_UI.app.signIn.title, 80),
        body: text(signIn.body, DEFAULT_MOBILE_UI.app.signIn.body, MOBILE_UI_LIMITS.body),
        actionLabel: text(signIn.actionLabel, DEFAULT_MOBILE_UI.app.signIn.actionLabel, 60),
        usernameLabel: text(signIn.usernameLabel, DEFAULT_MOBILE_UI.app.signIn.usernameLabel, 60),
        usernamePlaceholder: text(signIn.usernamePlaceholder, DEFAULT_MOBILE_UI.app.signIn.usernamePlaceholder, 100),
        passwordLabel: text(signIn.passwordLabel, DEFAULT_MOBILE_UI.app.signIn.passwordLabel, 60),
        passwordPlaceholder: text(signIn.passwordPlaceholder, DEFAULT_MOBILE_UI.app.signIn.passwordPlaceholder, 100),
        showPasswordLabel: text(signIn.showPasswordLabel, DEFAULT_MOBILE_UI.app.signIn.showPasswordLabel, 60),
        hidePasswordLabel: text(signIn.hidePasswordLabel, DEFAULT_MOBILE_UI.app.signIn.hidePasswordLabel, 60),
        dividerLabel: text(signIn.dividerLabel, DEFAULT_MOBILE_UI.app.signIn.dividerLabel, 30),
        googleActionLabel: text(signIn.googleActionLabel, DEFAULT_MOBILE_UI.app.signIn.googleActionLabel, 60),
        googleBrowserNote: text(signIn.googleBrowserNote, DEFAULT_MOBILE_UI.app.signIn.googleBrowserNote, MOBILE_UI_LIMITS.body),
        twoFactorEyebrow: text(signIn.twoFactorEyebrow, DEFAULT_MOBILE_UI.app.signIn.twoFactorEyebrow, 60),
        twoFactorTitle: text(signIn.twoFactorTitle, DEFAULT_MOBILE_UI.app.signIn.twoFactorTitle, 80),
        twoFactorBody: text(signIn.twoFactorBody, DEFAULT_MOBILE_UI.app.signIn.twoFactorBody, MOBILE_UI_LIMITS.body),
        codeLabel: text(signIn.codeLabel, DEFAULT_MOBILE_UI.app.signIn.codeLabel, 80),
        codePlaceholder: text(signIn.codePlaceholder, DEFAULT_MOBILE_UI.app.signIn.codePlaceholder, 100),
        verifyActionLabel: text(signIn.verifyActionLabel, DEFAULT_MOBILE_UI.app.signIn.verifyActionLabel, 60),
        backActionLabel: text(signIn.backActionLabel, DEFAULT_MOBILE_UI.app.signIn.backActionLabel, 60)
      },
      noAccount: {
        eyebrow: text(noAccount.eyebrow, DEFAULT_MOBILE_UI.app.noAccount.eyebrow, 60),
        title: text(noAccount.title, DEFAULT_MOBILE_UI.app.noAccount.title, 80),
        body: text(noAccount.body, DEFAULT_MOBILE_UI.app.noAccount.body, MOBILE_UI_LIMITS.body),
        actionLabel: text(noAccount.actionLabel, DEFAULT_MOBILE_UI.app.noAccount.actionLabel, 60)
      },
      copy: {
        workspaceLabel: text(copy.workspaceLabel, fallbackCopy.workspaceLabel, 40),
        contactFallback: text(copy.contactFallback, fallbackCopy.contactFallback, 60),
        noPreview: text(copy.noPreview, fallbackCopy.noPreview, 100),
        attachmentFallback: text(copy.attachmentFallback, fallbackCopy.attachmentFallback, 60),
        contentUnavailable: text(copy.contentUnavailable, fallbackCopy.contentUnavailable, 120),
        timeUnavailable: text(copy.timeUnavailable, fallbackCopy.timeUnavailable, 60),
        channelFallback: text(copy.channelFallback, fallbackCopy.channelFallback, 40),
        unassignedLabel: text(copy.unassignedLabel, fallbackCopy.unassignedLabel, 60),
        systemAuthor: text(copy.systemAuthor, fallbackCopy.systemAuthor, 60),
        unreadLabel: text(copy.unreadLabel, fallbackCopy.unreadLabel, 30),
        errorTitle: text(copy.errorTitle, fallbackCopy.errorTitle, 100),
        retryLabel: text(copy.retryLabel, fallbackCopy.retryLabel, 40),
        offlineToast: text(copy.offlineToast, fallbackCopy.offlineToast, 160)
      }
    },
    tabs: normalizeTabs(source.tabs),
    chrome: normalizeChrome(source.chrome),
    screens,
    capabilities: {
      send: capability(source.capabilities?.send, DEFAULT_MOBILE_UI.capabilities.send),
      create: capability(source.capabilities?.create, DEFAULT_MOBILE_UI.capabilities.create),
      olivia: capability(source.capabilities?.olivia, DEFAULT_MOBILE_UI.capabilities.olivia)
    },
    managerCapabilities: normalizeManagerCapabilities(source.managerCapabilities)
  };
}

function findComponent(config: MobileUiConfig, screen: MobileTab, id: string) {
  return config.screens[screen].components.find((component) => component.id === id);
}

function normalizeLegacyV1(source: MobileUiV1Config): MobileUiConfig {
  const converted = cloneDefault();
  converted.revision = "legacy-v1";
  converted.app.brandLabel = text(source.app?.brandLabel, converted.app.brandLabel, 40);
  converted.app.loadingMessage = text(source.app?.loadingMessage, converted.app.loadingMessage, 120);
  converted.app.signIn = {
    ...converted.app.signIn,
    eyebrow: text(source.app?.signIn?.eyebrow, converted.app.signIn.eyebrow, 60),
    title: text(source.app?.signIn?.title, converted.app.signIn.title, 80),
    body: text(source.app?.signIn?.body, converted.app.signIn.body, MOBILE_UI_LIMITS.body),
    actionLabel: text(source.app?.signIn?.actionLabel, converted.app.signIn.actionLabel, 60)
  };
  converted.tabs = normalizeTabs(source.tabs);

  const account = findComponent(converted, "home", "account-overview");
  if (account) {
    account.eyebrow = text(source.home?.activeAccountEyebrow, account.eyebrow || "", 60);
    account.labels = {
      ...(account.labels || {}),
      online: text(source.home?.connectedLabel, account.labels?.online || "Connected", 40),
      offline: text(source.home?.offlineLabel, account.labels?.offline || "Offline", 40)
    };
  }
  const preview = findComponent(converted, "home", "inbox-preview");
  if (preview) {
    preview.title = text(source.home?.inboxPreviewTitle, preview.title || "Inbox", 80);
    preview.labels = { ...(preview.labels || {}), empty: text(source.home?.inboxEmptyText, preview.labels?.empty || "", 180) };
    if (preview.action) preview.action.label = text(source.home?.inboxOpenLabel, preview.action.label, 40);
  }
  const summary = findComponent(converted, "home", "workspace-tools");
  if (summary) {
    summary.items = (source.home?.summaryCards || []).slice(0, MOBILE_UI_LIMITS.summaryItems).map((card, index) => {
      const fallback = summary.items?.[index] || summary.items?.[0]!;
      const destination = tabKey(card.targetTab);
      return {
        key: identifier(card.key, fallback.key),
        title: text(card.title, fallback.title, 80),
        status: text(card.status, fallback.status, 80),
        body: text(card.body, fallback.body, 220),
        icon: destination ? converted.tabs.find((tab) => tab.key === destination)?.icon || fallback.icon : fallback.icon,
        tone: fallback.tone,
        titleSource: card.key === "olivia" ? "active-companion-name" : fallback.titleSource || null,
        action: destination
          ? card.key === "olivia" && destination === "profile"
            ? { name: "open-profile-view", label: "Open companion settings", icon: "arrow-right", tab: null, profileView: "ai-customize" }
            : { name: "navigate", label: `Open ${text(card.title, fallback.title, 40)}`, icon: "arrow-right", tab: destination, profileView: null }
          : null
      };
    });
  }

  const heading = findComponent(converted, "inbox", "inbox-heading");
  if (heading) {
    heading.eyebrow = text(source.inbox?.eyebrow, heading.eyebrow || "", 60);
    heading.title = text(source.inbox?.title, heading.title || "Inbox", 80);
    if (heading.action) heading.action.label = text(source.inbox?.refreshLabel, heading.action.label, 40);
  }
  const list = findComponent(converted, "inbox", "conversation-list");
  if (list) {
    list.labels = {
      ...(list.labels || {}),
      loading: text(source.inbox?.loadingText, list.labels?.loading || "", 120),
      empty: text(source.inbox?.emptyText, list.labels?.empty || "", 180),
      loadMore: text(source.inbox?.loadMoreLabel, list.labels?.loadMore || "", 60)
    };
  }
  const detail = findComponent(converted, "inbox", "conversation-detail");
  if (detail) {
    detail.labels = {
      ...(detail.labels || {}),
      noSelection: text(source.inbox?.noSelectionText, detail.labels?.noSelection || "", 180),
      loading: text(source.inbox?.detailLoadingText, detail.labels?.loading || "", 120),
      loadEarlier: text(source.inbox?.loadEarlierLabel, detail.labels?.loadEarlier || "", 60),
      replyTitle: text(source.inbox?.replyDisabled?.title, detail.labels?.replyTitle || "", 100),
      replyBody: text(source.inbox?.replyDisabled?.body, detail.labels?.replyBody || "", MOBILE_UI_LIMITS.body)
    };
  }

  for (const tab of ["olivia", "create"] as const) {
    const placeholder = findComponent(converted, tab, tab === "olivia" ? "wctek-chat" : `${tab}-placeholder`);
    const legacy = source.placeholders?.[tab];
    if (placeholder && legacy) {
      placeholder.eyebrow = text(legacy.eyebrow, placeholder.eyebrow || "", 60);
      placeholder.title = text(legacy.title, placeholder.title || tab, 80);
      placeholder.body = text(legacy.body, placeholder.body || "", MOBILE_UI_LIMITS.body);
    }
  }
  const session = findComponent(converted, "profile", "session-actions");
  const logoutAction = session?.secondaryAction?.name === "logout" ? session.secondaryAction : session?.action;
  if (logoutAction?.name === "logout") logoutAction.label = text(source.profile?.logoutLabel, logoutAction.label, 40);
  converted.capabilities = {
    send: capability(source.capabilities?.send, converted.capabilities.send),
    create: capability(source.capabilities?.create, converted.capabilities.create),
    olivia: capability(source.capabilities?.olivia, converted.capabilities.olivia)
  };
  return converted;
}

export function normalizeMobileUiConfig(value: unknown): MobileUiConfig {
  const source = plainObject(value) && plainObject(value.ui) ? value.ui : value;
  if (!plainObject(source)) return cloneDefault();
  try {
    if (utf8ByteLength(JSON.stringify(source)) > MOBILE_UI_LIMITS.responseBytes) return cloneDefault();
  } catch (_) {
    return cloneDefault();
  }
  if (source.schemaVersion === "mobile-ui.v2") return normalizeV2(source);
  if (source.schemaVersion === "mobile-ui.v1") return normalizeLegacyV1(source as MobileUiV1Config);
  return cloneDefault();
}
